import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Trash2 } from "lucide-react";
import { TYPES, TYPE_LABEL } from "@/components/leave/shared";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const Num = ({ label, value, onChange, testid, placeholder }) => (
  <div>
    <Label className="label-eyebrow">{label}</Label>
    <Input type="number" step="0.5" value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className="rounded-none mt-1 h-10" data-testid={testid} />
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-2 text-xs py-1.5">
    <span>{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

const Freq = ({ value, onChange, testid }) => (
  <Select value={value || "yearly"} onValueChange={onChange}>
    <SelectTrigger className="rounded-none mt-1 h-10" data-testid={testid}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="yearly">Full credit at leave-year start</SelectItem><SelectItem value="monthly">Monthly (1/12 each month)</SelectItem></SelectContent>
  </Select>
);

export const HrPolicy = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/leave/policy").then((r) => setP(r.data)); }, []);
  if (!p) return <div className="text-sm text-[#9CA3AF]">Loading…</div>;
  const set = (k) => (v) => setP({ ...p, [k]: v });
  const setMap = (k, t) => (v) => setP({ ...p, [k]: { ...p[k], [t]: v } });
  const save = async () => {
    const { current_year, id, updated_at, updated_by, ...body } = p; // eslint-disable-line no-unused-vars
    try { setP((await api.put("/leave/policy", body)).data); toast.success("Leave policy saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-4" data-testid="hr-policy">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="flat-card p-5 space-y-3">
          <div className="label-eyebrow">Casual & Sick Leave</div>
          <Num label="Casual Leave per year" value={p.cl_annual} onChange={set("cl_annual")} placeholder="Not configured" testid="pol-cl-annual" />
          <div><Label className="label-eyebrow">CL credit frequency</Label><Freq value={p.cl_credit_frequency} onChange={set("cl_credit_frequency")} testid="pol-cl-freq" /></div>
          <Num label="Sick Leave per year" value={p.sl_annual} onChange={set("sl_annual")} placeholder="Not configured" testid="pol-sl-annual" />
          <div><Label className="label-eyebrow">SL credit frequency</Label><Freq value={p.sl_credit_frequency} onChange={set("sl_credit_frequency")} testid="pol-sl-freq" /></div>
          <Toggle label="Pro-rate first year for mid-year joiners" checked={p.prorate_first_year} onChange={set("prorate_first_year")} testid="pol-prorate" />
        </section>
        <section className="flat-card p-5 space-y-3">
          <div className="label-eyebrow">Earned Leave</div>
          <Num label="Eligible after (months of service)" value={p.el_eligibility_months} onChange={set("el_eligibility_months")} testid="pol-el-months" />
          <Num label="Initial credit on eligibility (days)" value={p.el_initial_days} onChange={set("el_initial_days")} testid="pol-el-initial" />
          <Num label="Credit per completed month after (days)" value={p.el_monthly_days} onChange={set("el_monthly_days")} testid="pol-el-monthly" />
          <div className="text-[11px] text-[#4B5563]">Calculated from each employee's joining date. Only completed months count.</div>
        </section>
        <section className="flat-card p-5 space-y-3">
          <div className="label-eyebrow">Leave Year & Rules</div>
          <div>
            <Label className="label-eyebrow">Leave year starts (MM-DD)</Label>
            <Input value={p.leave_year_start} onChange={(e) => set("leave_year_start")(e.target.value)} className="rounded-none mt-1 h-10" placeholder="04-01" data-testid="pol-year-start" />
            <div className="text-[11px] text-[#4B5563] mt-1" data-testid="pol-current-year">Current leave year: {fmtDate(p.current_year.start)} – {fmtDate(p.current_year.end)}</div>
          </div>
          <Toggle label="Allow negative leave balance" checked={p.allow_negative} onChange={set("allow_negative")} testid="pol-negative" />
          <Toggle label="Allow applying for future dates" checked={p.allow_advance} onChange={set("allow_advance")} testid="pol-advance" />
          <div>
            <Label className="label-eyebrow">Weekly offs</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {DAYS.map((d, i) => {
                const on = (p.weekly_offs || []).includes(i);
                return <button key={d} onClick={() => set("weekly_offs")(on ? p.weekly_offs.filter((x) => x !== i) : [...(p.weekly_offs || []), i])}
                  className={`px-2 h-8 text-xs font-bold border ${on ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "border-[#E5E7EB]"}`} data-testid={`pol-off-${d}`}>{d}</button>;
              })}
            </div>
          </div>
        </section>
      </div>
      <section className="flat-card p-5 overflow-x-auto">
        <div className="label-eyebrow mb-3">Per Leave Type</div>
        <table className="text-sm w-full min-w-[640px]">
          <thead><tr>{["", "Carry forward", "Max carry forward (days)", "Min notice (days)", "Document mandatory"].map((h) => <th key={h} className="text-left label-eyebrow pb-2">{h}</th>)}</tr></thead>
          <tbody>
            {TYPES.map((t) => (
              <tr key={t} className="border-t border-[#F3F4F6]">
                <td className="py-2 font-bold">{TYPE_LABEL[t]}</td>
                <td><Switch checked={!!p.carry_forward[t]} onCheckedChange={setMap("carry_forward", t)} data-testid={`pol-cf-${t}`} /></td>
                <td><Input type="number" disabled={t === "EL"} value={t === "EL" ? "" : p.max_carry_forward[t] ?? ""} placeholder={t === "EL" ? "Accumulates" : "No limit"} onChange={(e) => setMap("max_carry_forward", t)(e.target.value === "" ? null : Number(e.target.value))} className="rounded-none h-9 w-28" data-testid={`pol-maxcf-${t}`} /></td>
                <td><Input type="number" value={p.min_notice_days[t] ?? 0} onChange={(e) => setMap("min_notice_days", t)(Number(e.target.value || 0))} className="rounded-none h-9 w-24" data-testid={`pol-notice-${t}`} /></td>
                <td><Switch checked={!!p.doc_mandatory[t]} onCheckedChange={setMap("doc_mandatory", t)} data-testid={`pol-doc-${t}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <Button onClick={save} className="rounded-none bg-[#002FA7] uppercase tracking-widest text-xs font-bold h-10" data-testid="pol-save"><Save className="w-4 h-4 mr-2" /> Save Policy</Button>
      <Holidays />
    </div>
  );
};

const Holidays = () => {
  const [rows, setRows] = useState([]);
  const [h, setH] = useState({ date: "", name: "" });
  const load = () => api.get("/leave/holidays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/leave/holidays", h); setH({ date: "", name: "" }); load(); toast.success("Holiday added"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };
  return (
    <section className="flat-card p-5" data-testid="hr-holidays">
      <div className="label-eyebrow mb-3">Declared Holidays ({rows.length})</div>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input type="date" value={h.date} onChange={(e) => setH({ ...h, date: e.target.value })} className="rounded-none h-9 w-40" data-testid="holiday-date" />
        <Input value={h.name} onChange={(e) => setH({ ...h, name: e.target.value })} placeholder="Holiday name" className="rounded-none h-9 w-64" data-testid="holiday-name" />
        <Button onClick={add} className="rounded-none h-9 bg-[#0a0a0a] text-xs uppercase tracking-widest font-bold" data-testid="holiday-add">Add</Button>
      </div>
      <div className="divide-y divide-[#F3F4F6]">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center justify-between py-1.5 text-sm" data-testid={`holiday-${r.id}`}>
            <span><b>{fmtDate(r.date)}</b> · {r.name}</span>
            <button onClick={() => api.delete(`/leave/holidays/${r.id}`).then(load)} className="text-[#B91C1C]" data-testid={`holiday-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </section>
  );
};
